import type { PlayerCardComponentSpec, PlayerCardOption } from "../../Processing/types";

// ── Option catalog ──
// Single source of truth: drives the flat toggle-card list, the default
// selection, and the catalog ordering used to serialize requested_options.

export interface PlayerCardOptionDef {
  key: PlayerCardOption;
  label: string;
  description: string;
  defaultChecked: boolean;
}

export const PLAYER_CARD_OPTIONS: PlayerCardOptionDef[] = [
  { key: "name", label: "Name", description: "Player's first and last name", defaultChecked: true },
  { key: "headshot", label: "Headshot", description: "Player's profile photo", defaultChecked: true },
  { key: "position", label: "Position", description: "Player's position", defaultChecked: true },
  { key: "jersey", label: "Jersey", description: "Player's jersey number", defaultChecked: true },
  { key: "top_speed", label: "Top Speed", description: "Highest recorded top speed", defaultChecked: false },
  { key: "bodyweight_baseline", label: "Body weight Baseline", description: "Player's baseline bodyweight", defaultChecked: false },
  { key: "last_5_status", label: "Recent Avail.", description: "Player's availability status over the last 5 days", defaultChecked: false },
  { key: "active_injuries", label: "Active Injuries", description: "Any currently open injuries", defaultChecked: false },
  { key: "last_5_injuries", label: "Recent Injuries", description: "Last 5 recorded injuries for this player", defaultChecked: false },
];

/** Catalog order — requested_options is emitted in this order, not click order. */
export const CATALOG_ORDER: PlayerCardOption[] = PLAYER_CARD_OPTIONS.map((o) => o.key);

const DEFAULT_OPTIONS: PlayerCardOption[] = PLAYER_CARD_OPTIONS.filter(
  (o) => o.defaultChecked,
).map((o) => o.key);

// ── State ──

export interface PlayerCardFormState {
  playerId: number | null;
  options: PlayerCardOption[];
  /** UUID of the Slicer supplying the athlete, when bound. */
  singlePlayerControllerId?: string;
}

// ── Actions ──

export type PlayerCardAction =
  | { type: "SET_PLAYER"; playerId: number | null }
  | { type: "TOGGLE_OPTION"; option: PlayerCardOption }
  | { type: "SET_PLAYER_CONTROLLER"; controllerId?: string };

// ── Reducer ──

export function playerCardReducer(
  state: PlayerCardFormState,
  action: PlayerCardAction,
): PlayerCardFormState {
  switch (action.type) {
    case "SET_PLAYER":
      return { ...state, playerId: action.playerId };

    case "SET_PLAYER_CONTROLLER":
      return { ...state, singlePlayerControllerId: action.controllerId };

    case "TOGGLE_OPTION": {
      const has = state.options.includes(action.option);
      return {
        ...state,
        options: has
          ? state.options.filter((o) => o !== action.option)
          : [...state.options, action.option],
      };
    }

    default:
      return state;
  }
}

// ── Factories ──

export function createInitialState(): PlayerCardFormState {
  return { playerId: null, options: [...DEFAULT_OPTIONS] };
}

export function hydrateFromSpec(spec: PlayerCardComponentSpec): PlayerCardFormState {
  return {
    playerId: spec.player_id,
    options: [...spec.requested_options],
    singlePlayerControllerId: spec.singlePlayerControllerId,
  };
}

export function formStateToSpec(state: PlayerCardFormState): PlayerCardComponentSpec | null {
  if (state.playerId == null) return null;        // player required
  if (state.options.length === 0) return null;    // ≥1 option required
  return {
    type: "player_card",
    // Title is intentionally unused (no UI field); kept null so the backend
    // schema (validated on save) still receives the expected `display` shape.
    display: { title: null },
    player_id: state.playerId,
    requested_options: CATALOG_ORDER.filter((o) => state.options.includes(o)),
    singlePlayerControllerId: state.singlePlayerControllerId,
  };
}
